import { Box, Stack, Text } from '@mantine/core'
import {
  memo,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { countEffectiveBoggleGoldTilesInPath } from '~/boggle/engine/standard/effectiveSpecialTiles'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import classes from './ScoreFeedback.module.css'

export type BoggleScoreFeedbackTier = 'big' | 'jackpot' | 'medium' | 'small' | 'tiny'

type BoggleScoreFeedbackEvent = {
  delayMs: number
  /** Value shown as +N — tiles cleared by this accepted word. */
  displayDelta: number
  goldTilesUsed: number
  id: number
  label: null | string
  tier: BoggleScoreFeedbackTier
  variant: 'tiles'
}

export type BoggleScoreFeedbackState = {
  boardPulse: null | Pick<BoggleScoreFeedbackEvent, 'id' | 'tier'>
  events: BoggleScoreFeedbackEvent[]
  pendingEventCount: number
  scorePulseToken: number
}

const MAX_VISIBLE_SCORE_EVENTS = 2
const SCORE_FEEDBACK_REMOVAL_BUFFER_MS = 220
const BOARD_PULSE_DURATION_MS = 520
/** Tile count normalized for white → gold tint on floating feedback. */
const FEEDBACK_TILES_TO_COLOR_CAP = 14
function clampFeedback01(x: number) {
  return Math.min(1, Math.max(0, x))
}

/** Low → white, high → gold (opaque RGB). Reused by meter tier bonus tint. */
export function warmthAccentColor(progress: number): string {
  const t = clampFeedback01(progress)
  const lo = [255, 255, 255] as const
  const hi = [232, 189, 79] as const
  return `rgb(${Math.round(lo[0] + (hi[0] - lo[0]) * t)} ${Math.round(
    lo[1] + (hi[1] - lo[1]) * t,
  )} ${Math.round(lo[2] + (hi[2] - lo[2]) * t)})`
}

type BoggleScoreFeedbackTierTiming = {
  durationMs: number
}

const BOGGLE_SCORE_FEEDBACK_TIER_TIMING: Record<
  BoggleScoreFeedbackTier,
  BoggleScoreFeedbackTierTiming
> = {
  tiny: { durationMs: 1400 },
  small: { durationMs: 1400 },
  medium: { durationMs: 1640 },
  big: { durationMs: 1960 },
  jackpot: { durationMs: 2240 },
}

/** Tier-matched glow for the full-screen pulse on word accept (`ScreenFeedbackPulseLayer`). */
const BOGGLE_SCORE_FEEDBACK_SCREEN_BORDER_GLOW: Record<BoggleScoreFeedbackTier, string> =
  {
    tiny: 'rgba(248, 231, 161, 0.3)',
    small: 'rgba(248, 231, 161, 0.4)',
    medium: 'rgba(248, 231, 161, 0.5)',
    big: 'rgba(248, 231, 161, 0.6)',
    jackpot: 'rgba(248, 231, 161, 0.7)',
  }

export function getAcceptedScoreFeedbackEvents(
  previousState: BoggleGameState,
  nextState: BoggleGameState,
): BoggleScoreFeedbackEvent[] {
  if (
    nextState.lastAction.kind !== 'accepted' ||
    nextState.lastAction.atMs === previousState.lastAction.atMs
  ) {
    return []
  }

  const goldTilesUsed =
    previousState.selection.length === 0
      ? 0
      : countEffectiveBoggleGoldTilesInPath(previousState, previousState.selection)

  const tilesClearedDelta = Math.max(
    0,
    nextState.tilesCleared - previousState.tilesCleared,
  )

  const events: BoggleScoreFeedbackEvent[] = [
    {
      delayMs: 0,
      displayDelta: tilesClearedDelta,
      goldTilesUsed,
      id: nextState.lastAction.atMs,
      label: null,
      tier: getBoggleScoreFeedbackTier({
        feedbackDelta: tilesClearedDelta,
        goldTilesUsed,
      }),
      variant: 'tiles',
    },
  ]

  return events
}

export function getBoggleScoreFeedbackTier({
  feedbackDelta,
  goldTilesUsed,
}: {
  feedbackDelta: number
  goldTilesUsed: number
}) {
  const tierSignal = feedbackDelta + (goldTilesUsed > 0 ? 1 : 0)

  if (feedbackDelta === 1) {
    return 'tiny'
  }

  if (tierSignal <= 2) {
    return 'small'
  }

  if (tierSignal === 3) {
    return 'medium'
  }

  if (tierSignal <= 5) {
    return 'big'
  }

  return 'jackpot'
}

export function formatBoggleScoreFeedbackValue(value: number) {
  return value > 0 ? `+${value}` : String(value)
}

export function useBoggleScoreFeedback(store: BoggleStore): BoggleScoreFeedbackState {
  const [events, setEvents] = useState<BoggleScoreFeedbackEvent[]>([])
  const [boardPulse, setBoardPulse] = useState<null | Pick<
    BoggleScoreFeedbackEvent,
    'id' | 'tier'
  >>(null)
  const [pendingEventCount, setPendingEventCount] = useState(0)
  const [scorePulseToken, setScorePulseToken] = useState(0)
  const previousStateRef = useRef(store.getState())
  const timeoutHandlesRef = useRef<number[]>([])

  useEffect(() => {
    previousStateRef.current = store.getState()
    timeoutHandlesRef.current.forEach(handle => window.clearTimeout(handle))
    timeoutHandlesRef.current = []
    setPendingEventCount(0)

    const unsubscribe = store.subscribe(() => {
      const previousState = previousStateRef.current
      const nextState = store.getState()
      const nextEvents = getAcceptedScoreFeedbackEvents(previousState, nextState)

      if (nextEvents.length > 0) {
        const delayedEvents = nextEvents.filter(event => event.delayMs > 0)

        setPendingEventCount(current => current + delayedEvents.length)

        const showEvent = (event: BoggleScoreFeedbackEvent) => {
          const tierTiming = BOGGLE_SCORE_FEEDBACK_TIER_TIMING[event.tier]

          if (event.delayMs > 0) {
            setPendingEventCount(current => Math.max(0, current - 1))
          }
          setEvents(current => [...current, event].slice(-MAX_VISIBLE_SCORE_EVENTS))

          setBoardPulse({ id: event.id, tier: event.tier })
          setScorePulseToken(current => current + 1)
          timeoutHandlesRef.current.push(
            window.setTimeout(() => {
              setBoardPulse(current => (current?.id === event.id ? null : current))
            }, BOARD_PULSE_DURATION_MS),
          )

          timeoutHandlesRef.current.push(
            window.setTimeout(() => {
              setEvents(current => current.filter(entry => entry.id !== event.id))
            }, tierTiming.durationMs + SCORE_FEEDBACK_REMOVAL_BUFFER_MS),
          )
        }

        for (const event of nextEvents) {
          if (event.delayMs <= 0) {
            showEvent(event)
            continue
          }

          timeoutHandlesRef.current.push(
            window.setTimeout(() => {
              showEvent(event)
            }, event.delayMs),
          )
        }
      }

      previousStateRef.current = nextState
    })

    return () => {
      unsubscribe()
      timeoutHandlesRef.current.forEach(handle => window.clearTimeout(handle))
      timeoutHandlesRef.current = []
    }
  }, [store])

  return {
    boardPulse,
    events,
    pendingEventCount,
    scorePulseToken,
  }
}

const COMBO_FEEDBACK_DURATION_MS = 900

export type BoggleComboFeedbackEvent = {
  amount: number
  id: number
  kind: 'increment' | 'reset'
}

export function useBoggleComboFeedback(store: BoggleStore): BoggleComboFeedbackEvent[] {
  const [events, setEvents] = useState<BoggleComboFeedbackEvent[]>([])
  const previousComboRef = useRef(store.getState().combo)
  const timeoutHandlesRef = useRef<number[]>([])
  const eventIdRef = useRef(0)

  useEffect(() => {
    previousComboRef.current = store.getState().combo
    timeoutHandlesRef.current.forEach(handle => window.clearTimeout(handle))
    timeoutHandlesRef.current = []

    const unsubscribe = store.subscribe(() => {
      const nextCombo = store.getState().combo
      const previousCombo = previousComboRef.current

      if (nextCombo !== previousCombo) {
        let event: BoggleComboFeedbackEvent | null = null
        if (nextCombo === 0 && previousCombo > 0) {
          eventIdRef.current += 1
          event = { amount: 0, id: eventIdRef.current, kind: 'reset' }
        } else if (nextCombo > previousCombo) {
          eventIdRef.current += 1
          event = {
            amount: nextCombo - previousCombo,
            id: eventIdRef.current,
            kind: 'increment',
          }
        }

        if (event !== null) {
          const queuedEvent = event
          setEvents(current => [...current, queuedEvent])
          timeoutHandlesRef.current.push(
            window.setTimeout(() => {
              setEvents(current => current.filter(entry => entry.id !== queuedEvent.id))
            }, COMBO_FEEDBACK_DURATION_MS),
          )
        }
      }

      previousComboRef.current = nextCombo
    })

    return () => {
      unsubscribe()
      timeoutHandlesRef.current.forEach(handle => window.clearTimeout(handle))
      timeoutHandlesRef.current = []
    }
  }, [store])

  return events
}

export const ComboFeedbackLayer = memo(function ComboFeedbackLayer({
  events,
}: {
  events: BoggleComboFeedbackEvent[]
}) {
  if (events.length === 0) {
    return null
  }

  return (
    <Box aria-hidden className={classes.comboFeedbackLayer}>
      {events.map(event => (
        <Box
          className={`${classes.comboPop} ${
            event.kind === 'reset' ? classes.comboPopReset : classes.comboPopIncrement
          }`}
          key={event.id}
        >
          {event.kind === 'reset' ? 'Reset!' : `+${event.amount}`}
        </Box>
      ))}
    </Box>
  )
})

const TIMER_BONUS_FEEDBACK_DURATION_MS = 1800

export type BoggleTimerBonusFeedbackEvent = {
  bonusMs: number
  id: number
}

function formatTimerBonusLabel(bonusMs: number) {
  const seconds = bonusMs / 1000
  const rounded = Number.isInteger(seconds) ? String(seconds) : seconds.toFixed(1)
  return `+${rounded}s`
}

export function useBoggleTimerBonusFeedback(
  store: BoggleStore,
): BoggleTimerBonusFeedbackEvent[] {
  const [events, setEvents] = useState<BoggleTimerBonusFeedbackEvent[]>([])
  const previousRef = useRef<{
    intervalDeadlineMs: null | number
    intervalsRemaining: null | number
  }>({
    intervalDeadlineMs: store.getState().intervalDeadlineMs,
    intervalsRemaining: store.getState().intervalsRemaining,
  })
  const eventIdRef = useRef(0)
  const timeoutHandlesRef = useRef<number[]>([])

  useEffect(() => {
    const initial = store.getState()
    previousRef.current = {
      intervalDeadlineMs: initial.intervalDeadlineMs,
      intervalsRemaining: initial.intervalsRemaining,
    }
    timeoutHandlesRef.current.forEach(handle => window.clearTimeout(handle))
    timeoutHandlesRef.current = []

    const unsubscribe = store.subscribe(() => {
      const next = store.getState()
      const prev = previousRef.current
      const prevDeadline = prev.intervalDeadlineMs
      const nextDeadline = next.intervalDeadlineMs
      const inPrep =
        next.intervalPlayBeginsAtMs !== null && next.timeMs < next.intervalPlayBeginsAtMs

      if (
        next.phase === 'playing' &&
        !next.isInOvertime &&
        !inPrep &&
        prevDeadline !== null &&
        nextDeadline !== null &&
        nextDeadline > prevDeadline &&
        Object.is(next.intervalsRemaining, prev.intervalsRemaining)
      ) {
        const deltaMs = nextDeadline - prevDeadline
        if (deltaMs > 0 && deltaMs <= 120_000) {
          eventIdRef.current += 1
          const queued: BoggleTimerBonusFeedbackEvent = {
            bonusMs: deltaMs,
            id: eventIdRef.current,
          }
          setEvents(current => [...current, queued])
          timeoutHandlesRef.current.push(
            window.setTimeout(() => {
              setEvents(current => current.filter(entry => entry.id !== queued.id))
            }, TIMER_BONUS_FEEDBACK_DURATION_MS),
          )
        }
      }

      previousRef.current = {
        intervalDeadlineMs: next.intervalDeadlineMs,
        intervalsRemaining: next.intervalsRemaining,
      }
    })

    return () => {
      unsubscribe()
      timeoutHandlesRef.current.forEach(handle => window.clearTimeout(handle))
      timeoutHandlesRef.current = []
    }
  }, [store])

  return events
}

export const TimerBonusFeedbackLayer = memo(function TimerBonusFeedbackLayer({
  events,
}: {
  events: BoggleTimerBonusFeedbackEvent[]
}) {
  if (events.length === 0) {
    return null
  }

  return (
    <Box aria-hidden className={classes.timerBonusFeedbackLayer}>
      {events.map(event => (
        <Box
          className={classes.timerBonusPop}
          key={event.id}
          style={{
            // Keep in sync with the setTimeout that removes the pop (same constant).
            animationDuration: `${TIMER_BONUS_FEEDBACK_DURATION_MS}ms`,
          }}
        >
          {formatTimerBonusLabel(event.bonusMs)}
        </Box>
      ))}
    </Box>
  )
})

function getScoreEventStyle(
  pick: Pick<BoggleScoreFeedbackEvent, 'tier'>,
): CSSProperties {
  const timing = BOGGLE_SCORE_FEEDBACK_TIER_TIMING[pick.tier]

  return {
    '--score-feedback-duration': `${timing.durationMs}ms`,
  } as CSSProperties
}

export function getBoggleScoreHudValueClassName(scorePulseToken: number) {
  return scorePulseToken > 0 ? classes.scoreHudValue : undefined
}

export function hasActiveBoggleScoreFeedback({
  boardPulse,
  events,
  pendingEventCount,
}: Pick<BoggleScoreFeedbackState, 'boardPulse' | 'events' | 'pendingEventCount'>) {
  return boardPulse !== null || events.length > 0 || pendingEventCount > 0
}

function getScorePulseStyle(
  pulse: Pick<BoggleScoreFeedbackEvent, 'id' | 'tier'>,
): CSSProperties {
  return {
    ...getScoreEventStyle({ tier: pulse.tier }),
    '--score-feedback-board-glow': BOGGLE_SCORE_FEEDBACK_SCREEN_BORDER_GLOW[pulse.tier],
  } as CSSProperties
}

export const ScreenFeedbackPulseLayer = memo(function ScreenFeedbackPulseLayer({
  goldRushPulseKey,
  scorePulse,
}: {
  goldRushPulseKey: number
  scorePulse: null | Pick<BoggleScoreFeedbackEvent, 'id' | 'tier'>
}) {
  if (scorePulse === null && goldRushPulseKey <= 0) {
    return null
  }

  return (
    <Box aria-hidden className={classes.screenPulseLayer}>
      {scorePulse !== null ? (
        <Box
          className={classes.screenPulse}
          key={`screen-score-${scorePulse.id}`}
          style={getScorePulseStyle(scorePulse)}
        />
      ) : null}

      {goldRushPulseKey > 0 ? (
        <Box
          className={`${classes.screenPulse} ${classes.screenPulseGoldRush}`}
          key={`screen-gold-rush-${goldRushPulseKey}`}
        />
      ) : null}
    </Box>
  )
})

export type BoggleScoreFeedbackLayerPlacement = 'board' | 'hudTiles'

export const ScoreFeedbackLayer = memo(function ScoreFeedbackLayer({
  events,
  placement,
}: {
  events: BoggleScoreFeedbackEvent[]
  placement: BoggleScoreFeedbackLayerPlacement
}) {
  if (events.length === 0) {
    return null
  }

  const layerClass =
    placement === 'hudTiles' ? classes.feedbackLayerHudTiles : classes.feedbackLayerBoard
  const scorePopClass =
    placement === 'hudTiles' ? classes.scorePopHudTiles : classes.scorePopBoard

  return (
    <Box aria-hidden className={layerClass}>
      {events.map(event => {
        const style = getScoreEventStyle(event)
        const tint = clampFeedback01(
          (event.displayDelta + event.goldTilesUsed) / FEEDBACK_TILES_TO_COLOR_CAP,
        )
        const body: ReactNode = (
          <Text
            className={classes.scoreFeedbackTopLine}
            component="span"
            fw={800}
            lh={1.05}
            style={{ color: warmthAccentColor(tint) }}
          >
            {formatBoggleScoreFeedbackValue(event.displayDelta)}
          </Text>
        )

        return (
          <Box className={scorePopClass} key={event.id} style={style}>
            <Stack align="center" className={classes.scorePopWrap} gap={0}>
              {body}
            </Stack>
          </Box>
        )
      })}
    </Box>
  )
})
